import {
  ArrowBack,
  CheckCircleOutline,
  Close,
  ContentCopy,
  ExpandMore,
  HighlightOff,
  MeetingRoom,
  Refresh,
  Search,
} from "@mui/icons-material"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import moment from "moment"
import { FC, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import {
  Sst2Cell,
  Sst2Config,
  Sst2Door,
  Sst2Reservation,
  Sst2TemplateAssociation,
} from "shared/sstv2Types"
import { useGetSstv2LockerQuery } from "src/features/partners/partnersApi"
import { parseSst2Utc } from "../sst2DateUtils"
import { useNotification } from "./NotificationProvider"
import SaLayout from "./SaLayout"
import { UnstyledLink } from "./UnstyledLink"

// ── Status color map ─────────────────────────────────────

const StatusChipColor: Record<
  string,
  "default" | "primary" | "success" | "error" | "warning" | "info"
> = {
  DRAFT: "default",
  CONFIRMED: "primary",
  DELIVERED: "info",
  CLOSED: "success",
  EXPIRED: "error",
  CANCELLED: "warning",
  DELETED: "default",
}

// ── Helpers ──────────────────────────────────────────────

const extractSqsTag = (configs: Sst2Config[] | null | undefined): string | null => {
  if (!configs?.length) return null
  const opMode = configs.find((c) => c.type === "operation_mode")
  if (!opMode) return null
  const val = opMode.value
  if (typeof val === "object" && val !== null && "sqs_tag" in val && typeof val.sqs_tag === "string") {
    return val.sqs_tag
  }
  return null
}

/** Resolve door size from locker template cells. */
const resolveDoorSize = (
  doorNumber: number,
  templates: Sst2TemplateAssociation[] | null | undefined,
): string | null => {
  if (!templates?.length) return null
  for (const ta of templates) {
    if (!ta.template?.cells) continue
    const doorCells = ta.template.cells
      .filter((c: Sst2Cell) => c.cell_type === "DOOR")
      .sort((a: Sst2Cell, b: Sst2Cell) => a.id - b.id)
    if (doorNumber < doorCells.length) {
      return doorCells[doorNumber].size ?? null
    }
  }
  return null
}

/** Check if a door matches a search query (door ID, reservation ID, or reservation status). */
const doorMatchesSearch = (door: Sst2Door, query: string): boolean => {
  const q = query.toLowerCase()
  if (door.id.toLowerCase().includes(q)) return true
  if (String(door.door_number).includes(q)) return true
  if (door.door_label?.toLowerCase().includes(q)) return true
  for (const res of door.reservations ?? []) {
    if (res.id.toLowerCase().includes(q)) return true
    if (res.status.toLowerCase().includes(q)) return true
    if (res.reception_code?.toLowerCase().includes(q)) return true
    if (res.delivery_code?.toLowerCase().includes(q)) return true
    if (res.reservation_number != null && String(res.reservation_number).includes(q)) return true
  }
  return false
}

// ── Small reusable components ────────────────────────────

const InfoField: FC<{
  label: string
  value: string | number | null | undefined
  mono?: boolean
  copyable?: boolean
}> = ({ label, value, mono = false, copyable = false }) => {
  const handleCopy = useCallback(() => {
    if (value != null) navigator.clipboard.writeText(String(value))
  }, [value])

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" lineHeight={1.2}>
        {label}
      </Typography>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
        <Typography
          variant="body2"
          fontFamily={mono ? "monospace" : undefined}
          sx={{ wordBreak: "break-all", lineHeight: 1.4 }}
        >
          {value ?? "-"}
        </Typography>
        {copyable && value != null && (
          <Tooltip title="Copy">
            <IconButton size="small" onClick={handleCopy} sx={{ p: 0.25 }}>
              <ContentCopy sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Box>
  )
}

const SectionTitle: FC<{ children: React.ReactNode; count?: number }> = ({
  children,
  count,
}) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
    <Typography variant="h6" fontWeight={600} sx={{ fontSize: "1rem" }}>
      {children}
    </Typography>
    {count != null && (
      <Chip label={count} size="small" variant="outlined" />
    )}
  </Stack>
)

// ── Reservation Detail Drawer ────────────────────────────

const ReservationDetailDrawer: FC<{
  reservation: (Sst2Reservation & { _doorNumber?: number }) | null
  open: boolean
  onClose: () => void
}> = ({ reservation, open, onClose }) => {
  const { t } = useTranslation()
  if (!reservation) return null

  const chipColor = StatusChipColor[reservation.status] || "default"

  const expirationDate = parseSst2Utc(reservation.expiration)

  const fields: Array<{ label: string; value: string | number | null | undefined; mono?: boolean }> = [
    { label: "ID", value: reservation.id, mono: true },
    { label: t("status"), value: reservation.status },
    { label: "#", value: reservation.reservation_number },
    { label: "Type", value: reservation.reservation_type },
    { label: "Mode", value: reservation.locker_mode },
    { label: "Door ID", value: reservation.door_id, mono: true },
    { label: t("door"), value: reservation._doorNumber != null ? `#${reservation._doorNumber}` : null },
    { label: t("expiration"), value: expirationDate ? moment(expirationDate).format("YYYY-MM-DD HH:mm:ss") : null },
    { label: "Reception Code", value: reservation.reception_code, mono: true },
    { label: "Delivery Code", value: reservation.delivery_code, mono: true },
    { label: "Admin Code", value: reservation.admin_code, mono: true },
    { label: t("amount"), value: reservation.amount != null ? `$${reservation.amount.toFixed(2)}` : null },
    { label: "Tag", value: reservation.tag },
  ]

  return (
    <Drawer data-ui="UI-SL2W-249"
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: "400px",
          maxWidth: "90vw",
          maxHeight: "calc(100vh - 64px)",
          height: "calc(100vh - 64px)",
          paddingTop: "64px",
        },
      }}
    >
      <Stack direction="column" height="100%">
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: "divider" }}
        >
          <Stack spacing={0.5}>
            <Typography variant="subtitle1" fontWeight={700}>
              Reservation
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={reservation.status}
                size="small"
                color={chipColor}
                sx={{ fontWeight: 600, fontSize: "0.75rem" }}
              />
              {reservation.reservation_number != null && (
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  #{reservation.reservation_number}
                </Typography>
              )}
            </Stack>
          </Stack>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Stack>

        {/* Body */}
        <Box sx={{ flex: 1, overflow: "auto", px: 2.5, py: 2 }}>
          <Stack spacing={0}>
            {fields.map((f) => (
              <Box
                key={f.label}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "140px 1fr",
                  alignItems: "baseline",
                  py: 1,
                  borderBottom: 1,
                  borderColor: "grey.100",
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                  {f.label}
                </Typography>
                <Typography
                  variant="body2"
                  fontFamily={f.mono ? "monospace" : undefined}
                  sx={{ wordBreak: "break-all" }}
                >
                  {f.value ?? "-"}
                </Typography>
              </Box>
            ))}
          </Stack>

          {/* Expiration relative time */}
          {expirationDate && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: "grey.50", borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Expires{" "}
                <Typography component="span" variant="caption" fontWeight={600}>
                  {moment(expirationDate).fromNow()}
                </Typography>
                {" "}({moment(expirationDate).format("llll")})
              </Typography>
            </Box>
          )}
        </Box>
      </Stack>
    </Drawer>
  )
}

// ── Reservation Row ──────────────────────────────────────

const ReservationRow: FC<{
  reservation: Sst2Reservation
  onClick: () => void
}> = ({ reservation, onClick }) => {
  const chipColor = StatusChipColor[reservation.status] || "default"
  const expirationDate = parseSst2Utc(reservation.expiration)
  const expirationLabel = expirationDate
    ? moment(expirationDate).format("YYYY-MM-DD HH:mm")
    : "-"

  return (
    <TableRow hover sx={{ cursor: "pointer" }} onClick={onClick}>
      <TableCell>
        <Tooltip title={reservation.id}>
          <Typography variant="caption" fontFamily="monospace" sx={{ fontSize: "0.75rem" }}>
            {reservation.id.length > 16
              ? `${reservation.id.slice(0, 8)}...${reservation.id.slice(-6)}`
              : reservation.id}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell>
        <Chip
          label={reservation.status}
          size="small"
          color={chipColor}
          sx={{ fontWeight: 600, fontSize: "0.7rem", height: 22 }}
        />
      </TableCell>
      <TableCell>
        <Typography variant="caption">
          {reservation.reservation_number != null ? `#${reservation.reservation_number}` : "-"}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="caption">{reservation.reservation_type ?? "-"}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="caption">{reservation.locker_mode ?? "-"}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="caption">{expirationLabel}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="caption" fontFamily="monospace">
          {reservation.reception_code ?? "-"}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="caption" fontFamily="monospace">
          {reservation.delivery_code ?? "-"}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="caption" fontFamily="monospace">
          {reservation.admin_code ?? "-"}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="caption">
          {reservation.amount != null ? `$${reservation.amount.toFixed(2)}` : "-"}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="caption">{reservation.tag ?? "-"}</Typography>
      </TableCell>
    </TableRow>
  )
}

// ── Door Card ────────────────────────────────────────────

const DoorCard: FC<{
  door: Sst2Door
  doorSize: string | null
  onReservationClick: (res: Sst2Reservation, doorNumber: number) => void
  forceExpanded?: boolean
}> = ({ door, doorSize, onReservationClick, forceExpanded }) => {
  const { t } = useTranslation()
  const reservations = door.reservations ?? []
  const hasReservations = reservations.length > 0

  return (
    <Accordion
      defaultExpanded={forceExpanded ?? hasReservations}
      variant="outlined"
      disableGutters
      sx={{
        "&:before": { display: "none" },
        borderRadius: "8px !important",
        mb: 1,
        overflow: "hidden",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          bgcolor: hasReservations ? "primary.50" : "grey.50",
          "&:hover": { bgcolor: hasReservations ? "primary.100" : "grey.100" },
          minHeight: 48,
          "& .MuiAccordionSummary-content": { my: 0.5 },
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" width="100%">
          <MeetingRoom fontSize="small" color={hasReservations ? "primary" : "disabled"} />
          <Typography variant="subtitle2" fontWeight={600}>
            {t("door")} #{door.door_number}
            {door.door_label && door.door_label !== String(door.door_number) && ` (${door.door_label})`}
          </Typography>
          <Tooltip title="Door ID">
            <Typography variant="caption" fontFamily="monospace" color="text.secondary">
              {door.id}
            </Typography>
          </Tooltip>
          {door.cell_id != null && (
            <Chip
              label={`Cell: ${door.cell_id}`}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: "0.7rem" }}
            />
          )}
          {doorSize && (
            <Chip
              label={`Size: ${doorSize}`}
              size="small"
              variant="outlined"
              color="info"
              sx={{ height: 20, fontSize: "0.7rem" }}
            />
          )}
          <Chip
            label={`${reservations.length} ${t("reservations").toLowerCase()}`}
            size="small"
            variant="outlined"
            color={hasReservations ? "primary" : "default"}
            sx={{ height: 20, fontSize: "0.7rem", ml: "auto !important" }}
          />
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        {hasReservations ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.50" }}>
                  <TableCell><strong>ID</strong></TableCell>
                  <TableCell><strong>{t("status")}</strong></TableCell>
                  <TableCell><strong>#</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell><strong>Mode</strong></TableCell>
                  <TableCell><strong>{t("expiration")}</strong></TableCell>
                  <TableCell><strong>Reception</strong></TableCell>
                  <TableCell><strong>Delivery</strong></TableCell>
                  <TableCell><strong>Admin</strong></TableCell>
                  <TableCell><strong>{t("amount")}</strong></TableCell>
                  <TableCell><strong>Tag</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reservations.map((res: Sst2Reservation) => (
                  <ReservationRow
                    key={res.id}
                    reservation={res}
                    onClick={() => onReservationClick(res, door.door_number)}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              {t("noData")}
            </Typography>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  )
}

// ── Template Card ────────────────────────────────────────

const TemplateCard: FC<{ tmpl: Sst2TemplateAssociation }> = ({ tmpl }) => {
  const template = tmpl.template
  const doorCells = template?.cells?.filter((c: Sst2Cell) => c.cell_type === "DOOR") ?? []
  const nonDoorCells = template?.cells?.filter((c: Sst2Cell) => c.cell_type !== "DOOR") ?? []

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent sx={{ pb: "12px !important" }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {tmpl.name}
          </Typography>
          {tmpl.visual_order != null && (
            <Chip label={`Visual: ${tmpl.visual_order}`} size="small" variant="outlined" sx={{ height: 20, fontSize: "0.7rem" }} />
          )}
          {tmpl.assembled_order != null && (
            <Chip label={`Assembly: ${tmpl.assembled_order}`} size="small" variant="outlined" sx={{ height: 20, fontSize: "0.7rem" }} />
          )}
        </Stack>

        {template && (
          <>
            <Stack direction="row" spacing={3} sx={{ mb: 1 }}>
              <InfoField label="Height" value={template.height} />
              <InfoField label="Width" value={template.width} />
              <InfoField label="Depth" value={template.depth} />
              <InfoField label="Door Cells" value={doorCells.length} />
              {nonDoorCells.length > 0 && (
                <InfoField label="Other Cells" value={nonDoorCells.length} />
              )}
            </Stack>

            {doorCells.length > 0 && (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                {[...doorCells]
                  .sort((a: Sst2Cell, b: Sst2Cell) => a.id - b.id)
                  .map((cell: Sst2Cell) => (
                    <Chip
                      key={cell.id}
                      label={`${cell.size ?? "?"} (${cell.width}x${cell.height})`}
                      size="small"
                      variant="outlined"
                      sx={{ height: 22, fontSize: "0.7rem", fontFamily: "monospace" }}
                    />
                  ))}
              </Stack>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

// ── Config Card ──────────────────────────────────────────

const ConfigCard: FC<{ config: Sst2Config }> = ({ config }) => (
  <Card variant="outlined" sx={{ borderRadius: 2 }}>
    <CardContent sx={{ pb: "12px !important" }}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        {config.type}
      </Typography>
      <Box
        component="pre"
        sx={{
          bgcolor: "grey.50",
          borderRadius: 1,
          p: 1.5,
          m: 0,
          fontSize: "0.75rem",
          fontFamily: "monospace",
          overflow: "auto",
          maxHeight: 200,
          whiteSpace: "pre-wrap",
          wordBreak: "break-all",
        }}
      >
        {JSON.stringify(config.value, null, 2)}
      </Box>
    </CardContent>
  </Card>
)

// ── Grid-based detail row for Identity/Location ─────────

const GRID_COLS = "140px 1fr"

const DetailRow: FC<{
  label: string
  value: string | number | null | undefined
  mono?: boolean
  copyable?: boolean
}> = ({ label, value, mono = false, copyable = false }) => {
  const handleCopy = useCallback(() => {
    if (value != null) navigator.clipboard.writeText(String(value))
  }, [value])

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: GRID_COLS,
        alignItems: "baseline",
        py: 0.75,
        "&:not(:last-child)": { borderBottom: 1, borderColor: "grey.100" },
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={500}>
        {label}
      </Typography>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography
          variant="body2"
          fontFamily={mono ? "monospace" : undefined}
          sx={{ wordBreak: "break-all" }}
        >
          {value ?? "-"}
        </Typography>
        {copyable && value != null && (
          <Tooltip title="Copy">
            <IconButton size="small" onClick={handleCopy} sx={{ p: 0.25 }}>
              <ContentCopy sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Box>
  )
}

// ── Main Page Component ──────────────────────────────────

export const SaSstv2LockerPage: FC = () => {
  const { t } = useTranslation()
  const { lockerId } = useParams<{ lockerId: string }>()
  const { showNotification } = useNotification()

  // Door search
  const [doorSearch, setDoorSearch] = useState("")

  // Reservation drawer
  const [selectedReservation, setSelectedReservation] = useState<
    (Sst2Reservation & { _doorNumber?: number }) | null
  >(null)

  const {
    data: lockerResp,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetSstv2LockerQuery(
    { resourceId: lockerId ?? "" },
    { skip: !lockerId },
  )

  const locker = lockerResp?.data ?? null
  const currentSqsTag = locker ? extractSqsTag(locker.configs) : null

  const allDoors = useMemo(
    () => [...(locker?.doors ?? [])].sort((a: Sst2Door, b: Sst2Door) => a.door_number - b.door_number),
    [locker?.doors],
  )

  const filteredDoors = useMemo(() => {
    if (!doorSearch.trim()) return allDoors
    return allDoors.filter((d: Sst2Door) => doorMatchesSearch(d, doorSearch.trim()))
  }, [allDoors, doorSearch])

  const totalReservations = useMemo(
    () => allDoors.reduce((sum: number, d: Sst2Door) => sum + (d.reservations?.length ?? 0), 0),
    [allDoors],
  )

  const handleCopyId = useCallback(() => {
    if (lockerId) {
      navigator.clipboard.writeText(lockerId)
      showNotification("Locker ID copied", "success")
    }
  }, [lockerId, showNotification])

  const handleReservationClick = useCallback((res: Sst2Reservation, doorNumber: number) => {
    setSelectedReservation({ ...res, _doorNumber: doorNumber })
  }, [])

  return (
    <SaLayout selectedMenuItemLabelKey="sstv2Api">
      <Box sx={{ p: { xs: 2, sm: 3 }, pl: { sm: 4 }, mt: 1, maxWidth: 1400 }}>
        {/* ── Breadcrumb + Actions ── */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 3 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <UnstyledLink to="/sa/sstv2?tab=1">
              <IconButton size="small" sx={{ mr: 0.5 }}>
                <ArrowBack fontSize="small" />
              </IconButton>
            </UnstyledLink>
            <UnstyledLink to="/sa/sstv2">
              <Typography variant="body2" color="text.secondary" sx={{ "&:hover": { color: "primary.main", textDecoration: "underline" }, cursor: "pointer" }}>
                SST V2
              </Typography>
            </UnstyledLink>
            <Typography variant="body2" color="text.secondary">
              /
            </Typography>
            <UnstyledLink to="/sa/sstv2?tab=1">
              <Typography variant="body2" color="text.secondary" sx={{ "&:hover": { color: "primary.main", textDecoration: "underline" }, cursor: "pointer" }}>
                {t("lockers")}
              </Typography>
            </UnstyledLink>
            <Typography variant="body2" color="text.secondary">
              /
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {locker?.name || lockerId}
            </Typography>
            {(isLoading || isFetching) && <CircularProgress size={16} sx={{ ml: 1 }} />}
          </Stack>

          <Tooltip title={t("refresh")}>
            <IconButton onClick={() => refetch()} size="small" disabled={isLoading}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        {/* ── Error State ── */}
        {isError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load locker: {(error as any)?.data?.message ?? "Unknown error"}
          </Alert>
        )}

        {/* ── Loading State ── */}
        {isLoading && !locker && (
          <Stack spacing={3}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={120} />
            ))}
          </Stack>
        )}

        {/* ── Locker Detail ── */}
        {locker && (
          <Stack spacing={3}>
            {/* ── Header Card ── */}
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent sx={{ pb: "16px !important" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Typography variant="h5" fontWeight={700}>
                        {locker.name || `Locker #${locker.locker_number}`}
                      </Typography>
                      {locker.is_online ? (
                        <Chip
                          icon={<CheckCircleOutline sx={{ fontSize: 16 }} />}
                          label={t("online")}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      ) : (
                        <Chip
                          icon={<HighlightOff sx={{ fontSize: 16 }} />}
                          label={t("offline")}
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                      )}
                      <Chip
                        label={locker.is_enabled ? t("enabled") : t("deactivated")}
                        size="small"
                        color={locker.is_enabled ? "success" : "default"}
                      />
                    </Stack>

                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography
                        variant="caption"
                        fontFamily="monospace"
                        sx={{ bgcolor: "grey.100", px: 1, py: 0.25, borderRadius: 0.5 }}
                      >
                        {locker.id}
                      </Typography>
                      <Tooltip title="Copy ID">
                        <IconButton size="small" onClick={handleCopyId} sx={{ p: 0.25 }}>
                          <ContentCopy sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>

                  {/* Summary stats */}
                  <Stack direction="row" spacing={3} alignItems="center">
                    <Stack alignItems="center">
                      <Typography variant="h4" fontWeight={700} color="primary">
                        {allDoors.length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("doors")}
                      </Typography>
                    </Stack>
                    <Divider orientation="vertical" flexItem />
                    <Stack alignItems="center">
                      <Typography variant="h4" fontWeight={700} color="info.main">
                        {totalReservations}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("reservations")}
                      </Typography>
                    </Stack>
                    {currentSqsTag && (
                      <>
                        <Divider orientation="vertical" flexItem />
                        <Stack alignItems="center">
                          <Chip label={currentSqsTag} size="small" color="primary" />
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                            SQS Tag
                          </Typography>
                        </Stack>
                      </>
                    )}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            {/* ── Identity & Location (side by side, grid aligned) ── */}
            <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
              {/* Identity */}
              <Card variant="outlined" sx={{ flex: 1, borderRadius: 2 }}>
                <CardContent sx={{ pb: "16px !important" }}>
                  <SectionTitle>{t("identity")}</SectionTitle>
                  <Stack spacing={0}>
                    <DetailRow label="ID" value={locker.id} mono copyable />
                    <DetailRow label="UUID" value={locker.uuid} mono copyable />
                    <DetailRow label={t("lockerNumber")} value={locker.locker_number} />
                    <DetailRow label={t("name")} value={locker.name} />
                    <DetailRow label={t("serialNumber")} value={locker.serial_number} mono />
                    <DetailRow label={t("referenceId")} value={locker.reference_id} mono copyable />
                    <DetailRow label={t("description")} value={locker.description} />
                  </Stack>
                </CardContent>
              </Card>

              {/* Location */}
              <Card variant="outlined" sx={{ flex: 1, borderRadius: 2 }}>
                <CardContent sx={{ pb: "16px !important" }}>
                  <SectionTitle>{t("location")}</SectionTitle>
                  <Stack spacing={0}>
                    <DetailRow label={t("address")} value={locker.address} />
                    <DetailRow label="Apartment" value={locker.apartment} />
                    <DetailRow label={t("city")} value={locker.city} />
                    <DetailRow label={t("province")} value={locker.region} />
                    <DetailRow label={t("country")} value={locker.country} />
                    <DetailRow label={t("postalCode")} value={locker.postal_code} />
                    <DetailRow label={t("latitude")} value={locker.latitude} mono />
                    <DetailRow label={t("longitude")} value={locker.longitude} mono />
                    <DetailRow label={t("timezone")} value={locker.timezone} />
                  </Stack>
                </CardContent>
              </Card>
            </Stack>

            {/* ── Doors ── */}
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <SectionTitle count={allDoors.length}>{t("doors")}</SectionTitle>
                <TextField
                  size="small"
                  placeholder="Search doors, reservations, status..."
                  value={doorSearch}
                  onChange={(e) => setDoorSearch(e.target.value)}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                    },
                  }}
                  sx={{ width: 340 }}
                />
              </Stack>

              {doorSearch.trim() && filteredDoors.length !== allDoors.length && (
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                  Showing {filteredDoors.length} of {allDoors.length} doors
                </Typography>
              )}

              {filteredDoors.length > 0 ? (
                <Stack spacing={0}>
                  {filteredDoors.map((door: Sst2Door) => (
                    <DoorCard
                      key={door.id}
                      door={door}
                      doorSize={resolveDoorSize(door.door_number, locker.templates_association)}
                      onReservationClick={handleReservationClick}
                      forceExpanded={doorSearch.trim().length > 0 ? true : undefined}
                    />
                  ))}
                </Stack>
              ) : doorSearch.trim() ? (
                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                  No doors match "{doorSearch}"
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                  {t("noData")}
                </Typography>
              )}
            </Paper>

            {/* ── Templates & Configs (side by side) ── */}
            <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
              {/* Templates */}
              <Paper variant="outlined" sx={{ flex: 1, p: 2.5, borderRadius: 2 }}>
                <SectionTitle count={locker.templates_association?.length ?? 0}>
                  {t("templates")}
                </SectionTitle>
                {locker.templates_association && locker.templates_association.length > 0 ? (
                  <Stack spacing={1.5}>
                    {locker.templates_association.map((tmpl: Sst2TemplateAssociation, i: number) => (
                      <TemplateCard key={i} tmpl={tmpl} />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                    {t("noData")}
                  </Typography>
                )}
              </Paper>

              {/* Configs */}
              <Paper variant="outlined" sx={{ flex: 1, p: 2.5, borderRadius: 2 }}>
                <SectionTitle count={locker.configs?.length ?? 0}>
                  {t("configs")}
                </SectionTitle>
                {locker.configs && locker.configs.length > 0 ? (
                  <Stack spacing={1.5}>
                    {locker.configs.map((config: Sst2Config, i: number) => (
                      <ConfigCard key={i} config={config} />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                    {t("noData")}
                  </Typography>
                )}
              </Paper>
            </Stack>
          </Stack>
        )}
      </Box>

      {/* ── Reservation Detail Drawer ── */}
      <ReservationDetailDrawer
        reservation={selectedReservation}
        open={!!selectedReservation}
        onClose={() => setSelectedReservation(null)}
      />
    </SaLayout>
  )
}

export default SaSstv2LockerPage
